/**
 * AtlasWarSupplyModule — PLAYER-RUN SUPPLY FOR THE WAR, the carry (2026-09-24). Taken and listed at a town's contract
 * board (AtlasContractBoardModule); rules in server/src/lib/war_supply_ops.ts.
 *
 *   CARRYING  a chip under the wallet (the crates, the outpost's distance and bearing, the time left) and the crates
 *             themselves strapped to your hovercraft while you ride (a few boxes on the deck — the load you took).
 *   ARRIVING  riding within DELIVER_R_M of your side's outpost, E unloads them (`deliverWarSupply`): the battle feels
 *             it at once and the reward is paid.
 * Levers: `__warSupply()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { interactFocus } from '../lib/interact_focus.js';
import { onInteract, interactPrompt } from '../lib/interact_input.js';
import { townName } from '../lib/town_names.js';
import { compassOf, untilText } from '../lib/town_life.js';
import { act, ms } from './town/town_ui.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';

interface SupplyRow { runId: bigint; originSid: bigint; outpostId: bigint; cargo: string; crates: number; reward: bigint; state: string; taker: Identity; deadline: { microsSinceUnixEpoch: bigint } }
interface OutpostRow { outpostId: bigint; x: number; z: number }
interface VehicleLike { rideObject?(): THREE.Object3D | null }

const DELIVER_R_M = 35;
const CARGO_LABEL: Record<string, string> = { munitions: 'munitions', medical: 'medical supplies', rations: 'rations', parts: 'turret parts' };
const CRATE_COLOR: Record<string, number> = { munitions: 0x6a5a3a, medical: 0xd8d8d0, rations: 0x8a6a3a, parts: 0x5a6a72 };

export class AtlasWarSupplyModule implements IModule {
  readonly name = 'AtlasWarSupply';
  private app!: App;
  private chipEl: HTMLDivElement | null = null;
  private prompt: HTMLDivElement | null = null;
  private crates: THREE.Group | null = null;
  private cratesFor = '';
  private focused: bigint | null = null;
  /** F2: the bound Interact key AND the pad's (`atlas:place-requested`), one press — lib/interact_input.ts. */
  private offInteract: (() => void) | null = null;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('WarSupply', Phase.UI, 36, () => this.tick()));
    this.offInteract = onInteract(() => this.key());
    const c = document.createElement('div');
    c.id = 'war-supply-chip';
    c.className = 'ax-chip';
    c.style.cssText = 'position:fixed;top:152px;left:50%;transform:translateX(-50%);z-index:31;display:none;pointer-events:none;'
      + 'font:12px var(--ax-font);color:var(--ax-info);background:color-mix(in srgb, var(--ax-bg) 80%, transparent);padding:4px 10px';
    document.body.appendChild(c);
    this.chipEl = c;
    const p = document.createElement('div');
    p.id = 'war-supply-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-info) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    (window as unknown as { __warSupply?: unknown }).__warSupply = () => {
      const r = this.carrying();
      return { carrying: r ? { runId: String(r.runId), cargo: r.cargo, crates: r.crates } : null, cratesDrawn: this.crates?.children.length ?? 0, onRide: !!this.crates?.parent };
    };
  }

  private carrying(): SupplyRow | null {
    const me = atlas.identityHex;
    if (!me) return null;
    try {
      for (const r of (atlas.conn?.db as unknown as { warSupplyRun?: { iter(): Iterable<SupplyRow> } })?.warSupplyRun?.iter() ?? []) {
        if (r.state === 'taken' && r.taker.toHexString() === me) return r;
      }
    } catch { /* not subscribed */ }
    return null;
  }
  private outpost(id: bigint): OutpostRow | null {
    try {
      for (const o of (atlas.conn?.db as unknown as { battleOutpost?: { iter(): Iterable<OutpostRow> } })?.battleOutpost?.iter() ?? []) if (o.outpostId === id) return o;
    } catch { /* not subscribed */ }
    return null;
  }

  private tick(): void {
    const r = atlas.currentWorldId !== null && !atlas.spaceFlight ? this.carrying() : null;
    if (!r) { this.setChip(''); this.showPrompt(null); this.dropCrates(); this.focused = null; return; }
    const o = this.outpost(r.outpostId);
    const d = o ? Math.hypot(o.x - atlas.localX, o.z - atlas.localZ) : Infinity;
    const where = o ? `${d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} ${compassOf(o.x - atlas.localX, o.z - atlas.localZ)}` : 'the front';
    this.setChip(`Carrying ${r.crates} crates of ${CARGO_LABEL[r.cargo] ?? r.cargo} for the ${townName(r.originSid)} outpost — ${where} · due in ${untilText(ms(r.deadline) - Date.now())}${atlas.hoverRide ? '' : ' · ride your hovercraft'}`);
    this.strapCrates(r);
    // The unload prompt (riding, at the outpost).
    const focus = interactFocus();
    const id = `supply:${r.runId}`;
    if (o && atlas.hoverRide && d <= DELIVER_R_M) focus.claim({ id, dist: d, priority: 4, label: interactPrompt('unload the crates at the outpost') });
    const owns = o && atlas.hoverRide && d <= DELIVER_R_M && focus.owns(id);
    this.focused = owns ? r.runId : null;
    this.showPrompt(owns ? interactPrompt('unload the crates at the outpost') : null);
  }

  private key(): void {
    const runId = this.focused;
    if (runId === null || !interactFocus().owns(`supply:${runId}`)) return;
    void act(atlas.conn?.reducers.deliverWarSupply({ runId }), 'Delivered — the front feels it');
  }

  /** The crates ride on the hovercraft's deck while you carry the run. */
  private strapCrates(r: SupplyRow): void {
    const ride = (this.app.getModule('AtlasVehicle') as unknown as VehicleLike | undefined)?.rideObject?.() ?? null;
    if (!ride || !atlas.hoverRide) { if (this.crates?.parent) this.crates.removeFromParent(); return; }
    const key = `${r.runId}:${r.crates}`;
    if (!this.crates || this.cratesFor !== key) {
      this.dropCrates();
      const g = new THREE.Group();
      g.name = 'war-supply-crates';
      const mat = new THREE.MeshStandardMaterial({ color: CRATE_COLOR[r.cargo] ?? 0x6a5a3a, roughness: 0.85 });
      const band = new THREE.MeshStandardMaterial({ color: 0x2a2622, roughness: 0.6, metalness: 0.4 });
      const n = Math.min(6, Math.max(2, Math.round(r.crates / 1.5)));
      for (let i = 0; i < n; i++) {
        const box = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.42, 0.55), mat);
        const col = i % 3, lay = Math.floor(i / 3);
        box.position.set((col - 1) * 0.6, 0.21 + lay * 0.44, lay * 0.08);
        box.rotation.y = (i % 2 ? 0.12 : -0.08);
        const strap = new THREE.Mesh(new THREE.BoxGeometry(0.57, 0.06, 0.57), band);
        strap.position.copy(box.position);
        g.add(box, strap);
      }
      this.crates = g;
      this.cratesFor = key;
    }
    if (this.crates.parent !== ride) {
      // MEASURE, THEN ATTACH (2026-09-25): the crates were added to the ride BEFORE its bounds were taken, so the box
      // held the crates themselves (standing at the ride's origin) and the ride's shield shell / haze quad, and it was a
      // WORLD box read as local (a yawed ride swapped its x and z extents). The hull alone is measured in the ride's own
      // frame, and the load is seated by its bottom-centre (the PIVOT law) on the deck.
      const box = this.hullBoxLocal(ride);
      const ws = ride.getWorldScale(new THREE.Vector3()).x || 1;
      if (box.isEmpty()) this.crates.position.set(0, 0.6 / ws, 0);
      else {
        const size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
        this.crates.position.set(c.x, box.min.y + size.y * 0.55, c.z + size.z * 0.12); // the deck: a little over half the hull's height, a little aft
      }
      this.crates.scale.setScalar(1 / ws); // real metres whatever the ride's fit scale
      ride.add(this.crates);
    }
  }

  /** The ride's HULL bounds in the ride's own frame — the crates and the ride's VFX (shield shell, haze quad) left out. */
  private hullBoxLocal(ride: THREE.Object3D): THREE.Box3 {
    ride.updateMatrixWorld(true);
    const inv = new THREE.Matrix4().copy(ride.matrixWorld).invert();
    const rel = new THREE.Matrix4();
    const box = new THREE.Box3(), part = new THREE.Box3();
    const skip = new Set(['war-supply-crates', 'ride-shield', 'ride-haze']);
    const walk = (o: THREE.Object3D): void => {
      if (skip.has(o.name)) return;
      const m = o as THREE.Mesh;
      if (m.isMesh && m.geometry) {
        if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
        if (m.geometry.boundingBox) box.union(part.copy(m.geometry.boundingBox).applyMatrix4(rel.multiplyMatrices(inv, m.matrixWorld)));
      }
      for (const ch of o.children) walk(ch);
    };
    for (const ch of ride.children) walk(ch);
    return box;
  }

  private dropCrates(): void {
    if (!this.crates) return;
    // Detached now, freed past the submit window — the materials too (2026-09-25: the crate and band materials were
    // never disposed, and the geometry was freed the same frame it may still have been drawn).
    deferDisposeTree(this.crates);
    this.crates = null;
    this.cratesFor = '';
  }

  private setChip(text: string): void {
    const c = this.chipEl;
    if (!c) return;
    if (!text) { if (c.style.display !== 'none') c.style.display = 'none'; return; }
    if (c.textContent !== text) c.textContent = text;
    if (c.style.display !== 'block') c.style.display = 'block';
  }
  private showPrompt(text: string | null): void {
    const p = this.prompt;
    if (!p) return;
    const show = text !== null;
    if ((p.style.display !== 'none') !== show) p.style.display = show ? 'block' : 'none';
    if (show && p.textContent !== text) p.textContent = text;
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    this.dropCrates();
    this.chipEl?.remove();
    this.prompt?.remove();
  }
}

